/**
 * Player View Component
 *
 * Main view for quiz participants with question display and answer submission.
 */

import { useState } from 'react';
import { useLiveQuizPlayer } from '../../../hooks/useLiveQuizPlayer';
import { ConnectionStatus } from '../shared/ConnectionStatus';
import { CountdownTimer } from '../shared/CountdownTimer';
import { AnswerButtons } from '../shared/AnswerButtons';
import { ScorePopup } from '../shared/ScorePopup';
import { Leaderboard } from '../shared/Leaderboard';
import { PlayerLobby } from './PlayerLobby';
import { AnswerFeedback } from './AnswerFeedback';

interface PlayerViewProps {
  sessionId: string;
  participantId: string;
  onKicked?: (reason?: string) => void;
  onLeave?: () => void;
}

export function PlayerView({ sessionId, participantId, onKicked, onLeave }: PlayerViewProps) {
  const [showScorePopup, setShowScorePopup] = useState(false);

  const {
    connected,
    connecting,
    error,
    gamePin,
    status,
    settings,
    nickname,
    currentQuestionIndex: _currentQuestionIndex,
    totalQuestions: _totalQuestions,
    currentQuestion,
    timeRemaining,
    isReady,
    myAnswer,
    myScore,
    myRank: _myRank,
    myStreak,
    lastResult,
    participantCount,
    answeredCount,
    correctAnswer,
    leaderboard,
    myLeaderboardEntry,
    canAnswer,
    isWaiting,
    setReady,
    submitAnswer,
    reconnect,
  } = useLiveQuizPlayer({
    sessionId,
    participantId,
    onKicked,
  });

  // These values are available for future UI enhancements
  void _currentQuestionIndex;
  void _totalQuestions;
  void _myRank;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-purple-700 to-indigo-800 flex flex-col">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {nickname && (
              <span className="text-white font-medium">{nickname}</span>
            )}
            <ConnectionStatus
              connected={connected}
              connecting={connecting}
              error={error}
              onReconnect={reconnect}
            />
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-purple-300">Score</p>
              <p className="text-xl font-bold text-white">{myScore.toLocaleString()}</p>
            </div>
            {myStreak >= 2 && (
              <div className="flex items-center gap-1 text-orange-400">
                <span>🔥</span>
                <span className="font-bold">{myStreak}</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        {/* Lobby */}
        {status === 'lobby' && (
          <PlayerLobby
            gamePin={gamePin || ''}
            nickname={nickname || ''}
            participantCount={participantCount}
            isReady={isReady}
            onReady={setReady}
            onLeave={onLeave}
          />
        )}

        {/* Starting */}
        {status === 'starting' && (
          <div className="text-center">
            <h2 className="text-4xl font-bold text-white mb-4">Get Ready!</h2>
            <div className="text-6xl animate-pulse">🎮</div>
          </div>
        )}

        {/* Question - Can Answer */}
        {status === 'question' && canAnswer && currentQuestion && (
          <div className="w-full max-w-xl">
            {/* Timer */}
            <div className="flex justify-center mb-6">
              <CountdownTimer
                seconds={timeRemaining}
                totalSeconds={settings?.questionTimeSeconds || 20}
                size="lg"
              />
            </div>

            {/* Question (if shown on player device) */}
            {settings?.showQuestionOnPlayerDevice && (
              <h2 className="text-xl font-bold text-white text-center mb-6">
                {currentQuestion.question}
              </h2>
            )}

            {/* Answer Options */}
            {currentQuestion.options && (
              <AnswerButtons
                options={currentQuestion.options}
                onSelect={submitAnswer}
              />
            )}

            {/* Text/Number Input */}
            {currentQuestion.type === 'text' && (
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Type your answer..."
                  className="w-full p-4 text-lg bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      submitAnswer((e.target as HTMLInputElement).value);
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                    if (input?.value) submitAnswer(input.value);
                  }}
                  className="w-full p-4 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-colors"
                >
                  Submit
                </button>
              </div>
            )}

            {currentQuestion.type === 'number' && (
              <div className="space-y-4">
                <input
                  type="number"
                  placeholder="Enter a number..."
                  className="w-full p-4 text-lg bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      submitAnswer(Number((e.target as HTMLInputElement).value));
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    const input = (e.target as HTMLElement).previousElementSibling as HTMLInputElement;
                    if (input?.value) submitAnswer(Number(input.value));
                  }}
                  className="w-full p-4 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-colors"
                >
                  Submit
                </button>
              </div>
            )}

            {currentQuestion.type === 'true_false' && (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => submitAnswer(true)}
                  className="p-8 bg-green-500 hover:bg-green-400 text-white font-bold text-2xl rounded-xl transition-colors"
                >
                  True
                </button>
                <button
                  onClick={() => submitAnswer(false)}
                  className="p-8 bg-red-500 hover:bg-red-400 text-white font-bold text-2xl rounded-xl transition-colors"
                >
                  False
                </button>
              </div>
            )}
          </div>
        )}

        {/* Waiting for others */}
        {status === 'question' && isWaiting && (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white/10 flex items-center justify-center">
              <svg className="w-10 h-10 text-purple-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Answer Submitted!</h2>
            <p className="text-purple-300">Waiting for other players...</p>
            <p className="text-sm text-purple-400 mt-4">
              {answeredCount} of {participantCount} answered
            </p>
          </div>
        )}

        {/* Answer Revealed */}
        {status === 'reveal' && lastResult && (
          <AnswerFeedback
            correct={lastResult.correct}
            points={lastResult.points}
            totalScore={lastResult.totalScore}
            streak={lastResult.streak}
            rank={lastResult.rank}
            totalParticipants={participantCount}
            correctAnswer={correctAnswer}
            myAnswer={myAnswer}
            streakThreshold={settings?.streakThreshold || 3}
            questionOptions={currentQuestion?.options}
          />
        )}

        {/* Leaderboard */}
        {status === 'leaderboard' && (
          <div className="w-full max-w-md">
            <h2 className="text-2xl font-bold text-white text-center mb-6">
              Leaderboard
            </h2>
            <Leaderboard
              entries={leaderboard}
              maxEntries={10}
              highlightParticipantId={participantId}
              showMovement
            />
            {myLeaderboardEntry && (
              <div className="mt-6 p-4 bg-white/10 rounded-xl text-center">
                <p className="text-purple-300">You are in</p>
                <p className="text-4xl font-bold text-white">
                  #{myLeaderboardEntry.rank}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Paused */}
        {status === 'paused' && (
          <div className="text-center">
            <div className="text-6xl mb-4">⏸️</div>
            <h2 className="text-2xl font-bold text-yellow-400">Game Paused</h2>
            <p className="text-purple-300 mt-2">Waiting for host to resume...</p>
          </div>
        )}

        {/* Game Ended */}
        {status === 'ended' && (
          <div className="w-full max-w-md text-center">
            <h2 className="text-3xl font-bold text-white mb-2">Game Over!</h2>

            {myLeaderboardEntry && (
              <div className="my-8">
                <p className="text-purple-300 mb-2">You finished</p>
                <div className="text-6xl font-bold text-white mb-2">
                  #{myLeaderboardEntry.rank}
                </div>
                <p className="text-2xl text-purple-300">
                  {myScore.toLocaleString()} points
                </p>
              </div>
            )}

            <Leaderboard
              entries={leaderboard}
              maxEntries={5}
              highlightParticipantId={participantId}
              compact
            />

            {onLeave && (
              <button
                onClick={onLeave}
                className="mt-8 px-8 py-3 bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-xl transition-colors"
              >
                Play Again
              </button>
            )}
          </div>
        )}
      </main>

      {/* Score Popup */}
      {showScorePopup && lastResult && (
        <ScorePopup
          correct={lastResult.correct}
          points={lastResult.points}
          streak={lastResult.streak}
          streakThreshold={settings?.streakThreshold || 3}
          onAnimationEnd={() => setShowScorePopup(false)}
        />
      )}
    </div>
  );
}

export default PlayerView;
